/**
 * HVM Quantity Breaks nativo
 * ------------------------------------------------------------------
 * Componente sin dependencias que acompaña al snippet
 * snippets/hvm-quantity-breaks.liquid. Soporta las dos variantes de
 * diseño (minimal / classic), cada una con dos estados de frecuencia
 * (compra única / suscripción).
 *
 * Responsabilidades:
 * - Escribir `quantity` y `selling_plan` en el product form principal.
 * - Sincronizar precios mostrados cuando cambia el tier, la frecuencia
 *   o el selling plan, usando el JSON embebido por variante:
 *   - minimal: totales y precio unitario de cada fila de frecuencia,
 *     más el tachado de compra única dentro de Suscríbete.
 *   - classic: total y tachado (compare-at) de cada fila de tier según
 *     la frecuencia activa.
 * - Delegación de eventos a nivel documento: sobrevive al re-render del
 *   bloque en cambios de variante (Section Rendering API) y en el editor.
 *
 * Este componente NUNCA calcula descuentos: los precios base vienen de
 * Liquid (variant.price, compare_at_price y selling_plan_allocations);
 * solo multiplica precio x cantidad. Todo precio real lo fija Shopify.
 */
(function () {
  'use strict';

  var PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = typeof precision === 'undefined' ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || '${{amount}}';
    var match = formatString.match(PLACEHOLDER_REGEX);
    var value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(PLACEHOLDER_REGEX, value);
  }

  function readData(root) {
    var node = root.querySelector('[data-hvm-data]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[HVM QB] JSON inválido', error);
      return null;
    }
  }

  function setSelectedClass(nodes, selectedNode, className) {
    nodes.forEach(function (node) {
      node.classList.toggle(className, node === selectedNode);
    });
  }

  function currentPlan(data, root) {
    var select = root.querySelector('[data-hvm-plan-select]');
    if (!select || !data || !data.plans.length) return null;
    var option = select.selectedOptions[0];
    if (!option) return null;
    var id = option.getAttribute('value');
    return (
      data.plans.find(function (plan) {
        return String(plan.id) === id;
      }) || null
    );
  }

  function getQty(root) {
    var checked = root.querySelector('[data-hvm-tier-qty]:checked');
    return checked ? parseInt(checked.value, 10) || 1 : 1;
  }

  function isSubscriptionSelected(root) {
    var checked = root.querySelector('[data-hvm-freq]:checked');
    return checked ? checked.value === 'subscription' : false;
  }

  function setMoney(root, selector, cents, moneyFormat) {
    var node = root.querySelector(selector);
    if (node) node.textContent = formatMoney(cents, moneyFormat);
  }

  /**
   * "Agregar al carrito · $1,656" en el submit del product form.
   * El label base se conserva en data-hvm-base-label; si el theme cambia
   * el texto por su cuenta (Agotado, PRE-ORDENAR, cambio de idioma) se
   * adopta como base nueva. Botón deshabilitado (agotado) no se toca.
   */
  function refreshCtaTotal(data, totalCents) {
    if (!data.ctaTotal || !data.ctaMoneyFormat) return;
    var form = document.getElementById(data.formId);
    if (!form) return;
    var button = form.querySelector('button[name="add"]');
    if (!button || button.disabled) return;
    var label = button.querySelector('span');
    if (!label) return;

    var current = label.textContent.replace(/\s+/g, ' ').trim();
    var applied = button.getAttribute('data-hvm-cta-applied');
    if (current !== applied) {
      button.setAttribute('data-hvm-base-label', current);
    }
    var base = button.getAttribute('data-hvm-base-label') || current;
    var next = base + ' · ' + formatMoney(totalCents, data.ctaMoneyFormat);
    label.textContent = next;
    button.setAttribute('data-hvm-cta-applied', next);
  }

  function refreshPrices(root) {
    var data = readData(root);
    if (!data || !data.moneyFormat) return;

    var moneyFormat = data.moneyFormat;
    var qty = getQty(root);
    var plan = currentPlan(data, root);
    var singlePrice = data.variantPrice;

    // Filas de frecuencia (minimal)
    setMoney(root, '[data-hvm-single-total]', singlePrice * qty, moneyFormat);
    setMoney(root, '[data-hvm-single-unit]', singlePrice, moneyFormat);
    if (plan) {
      setMoney(root, '[data-hvm-sub-total-inline]', plan.price * qty, moneyFormat);
      setMoney(root, '[data-hvm-sub-unit-inline]', plan.price, moneyFormat);
      setMoney(root, '[data-hvm-sub-total]', plan.price * qty, moneyFormat);
      setMoney(root, '[data-hvm-sub-unit]', plan.price, moneyFormat);
      setMoney(root, '[data-hvm-sub-compare-total]', singlePrice * qty, moneyFormat);
      setMoney(root, '[data-hvm-sub-compare-unit]', singlePrice, moneyFormat);
    }

    // Filas de tier (classic): total según frecuencia activa + tachado compare-at
    var unitPrice = singlePrice;
    if (isSubscriptionSelected(root) && plan) {
      unitPrice = plan.price;
    }
    root.querySelectorAll('[data-hvm-tier-row]').forEach(function (row) {
      var rowQty = parseInt(row.getAttribute('data-hvm-qty'), 10) || 1;
      var total = row.querySelector('[data-hvm-tier-total]');
      if (total) total.textContent = formatMoney(unitPrice * rowQty, moneyFormat);
      var compare = row.querySelector('[data-hvm-tier-compare]');
      if (compare && data.compareAtPrice) {
        compare.textContent = formatMoney(data.compareAtPrice * rowQty, moneyFormat);
      }
    });

    refreshCtaTotal(data, unitPrice * qty);
  }

  function syncQuantityInputs(root, qty) {
    var ownInput = root.querySelector('[data-hvm-qty-input]');
    if (ownInput) ownInput.value = qty;

    // Si el form tiene un input de cantidad visible (bloque quantity_selector),
    // mantenerlo sincronizado para evitar valores contradictorios en FormData.
    var formId = root.getAttribute('data-hvm-form');
    if (!formId) return;
    var formQuantityInputs = document.querySelectorAll(
      'input[name="quantity"][form="' + formId + '"]:not([data-hvm-qty-input])'
    );
    formQuantityInputs.forEach(function (input) {
      if (input.value !== String(qty)) {
        input.value = qty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function handleTierChange(root) {
    var qty = getQty(root);

    var checkedTier = root.querySelector('[data-hvm-tier-qty]:checked');
    setSelectedClass(
      Array.prototype.slice.call(root.querySelectorAll('.hvm-tier')),
      checkedTier ? checkedTier.closest('.hvm-tier') : null,
      'hvm-tier--selected'
    );
    syncQuantityInputs(root, qty);
    refreshPrices(root);
  }

  function handleFrequencyChange(root, input) {
    var isSubscription = input.value === 'subscription';
    var planInput = root.querySelector('[data-hvm-plan-input]');
    var select = root.querySelector('[data-hvm-plan-select]');

    if (planInput) {
      if (isSubscription && select) {
        planInput.value = select.value;
        planInput.disabled = false;
      } else {
        // Deshabilitado = excluido del FormData: nunca viaja un selling_plan vacío
        planInput.value = '';
        planInput.disabled = true;
      }
    }

    setSelectedClass(
      Array.prototype.slice.call(root.querySelectorAll('.hvm-freq')),
      input.closest('.hvm-freq'),
      'hvm-freq--selected'
    );
    refreshPrices(root);
  }

  function handlePlanChange(root) {
    var planInput = root.querySelector('[data-hvm-plan-input]');
    var select = root.querySelector('[data-hvm-plan-select]');

    if (planInput && select && isSubscriptionSelected(root)) {
      planInput.value = select.value;
      planInput.disabled = false;
    }
    refreshPrices(root);
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var root = target.closest('[data-hvm-qb]');
    if (!root) return;

    if (target.matches('[data-hvm-tier-qty]')) {
      handleTierChange(root);
    } else if (target.matches('[data-hvm-freq]')) {
      handleFrequencyChange(root, target);
    } else if (target.matches('[data-hvm-plan-select]')) {
      handlePlanChange(root);
    }
  });

  // Pase inicial (el server no renderiza el total en el CTA) y re-aplicación
  // tras el re-render del bloque por cambio de variante (product-info.js
  // dispara hvm:qb:rerender después de reemplazar los nodos).
  function refreshAll() {
    document.querySelectorAll('[data-hvm-qb]').forEach(function (root) {
      refreshPrices(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAll);
  } else {
    refreshAll();
  }
  document.addEventListener('hvm:qb:rerender', refreshAll);
})();
